import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Folder as ProjectIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { Team, Project } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const HistoricalAnalytics: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [teams, setTeams] = useState<Team[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  
  const [analytics, setAnalytics] = useState<any>(null);
  const [velocity, setVelocity] = useState<any>(null);
  const [insights, setInsights] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTeams();
    loadProjects();
  }, []);

  useEffect(() => {
    if (teams.length > 0 || projects.length > 0) {
      loadAnalytics();
    }
  }, [selectedTeam, selectedProject, dateRange]);

  const loadTeams = async () => {
    try {
      // For now, we'll load teams for all projects
      const projects = await apiService.getProjects();
      const allTeams: Team[] = [];
      
      for (const project of projects) {
        try {
          const teams = await apiService.getTeams(project.id);
          allTeams.push(...teams);
        } catch (err) {
          console.error(`Failed to load teams for project ${project.id}:`, err);
        }
      }
      
      setTeams(allTeams);
    } catch (err) {
      console.error('Failed to load teams:', err);
    }
  };

  const loadProjects = async () => {
    try {
      const projects = await apiService.getProjects();
      setProjects(projects || []);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (selectedTeam) params.append('teamId', selectedTeam);
      if (selectedProject) params.append('projectId', selectedProject);
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);

      const [analyticsRes, insightsRes] = await Promise.all([
        apiService.getAccuracyAnalytics(params.toString()),
        apiService.getEstimationInsights(params.toString())
      ]);

      setAnalytics(analyticsRes);
      setInsights(insightsRes);

      if (selectedTeam) {
        const velocityRes = await apiService.getTeamVelocity(selectedTeam);
        setVelocity(velocityRes);
      }
    } catch (err) {
      setError('Failed to load analytics data');
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 80) return 'success';
    if (accuracy >= 60) return 'warning';
    return 'error';
  };

  const getAccuracyIcon = (accuracy: number) => {
    if (accuracy >= 80) return <CheckCircleIcon />;
    if (accuracy >= 60) return <WarningIcon />;
    return <ErrorIcon />;
  };

  const formatPercentage = (value: number) => {
    return `${Math.abs(value).toFixed(1)}%`;
  };

  const renderAccuracyOverview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <AssessmentIcon color="primary" />
              <Typography variant="h6">Total Stories</Typography>
            </Box>
            <Typography variant="h4" color="primary">
              {analytics?.totalStories || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed stories
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <TrendingUpIcon color="primary" />
              <Typography variant="h6">Avg Accuracy</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {analytics?.averageAccuracy && getAccuracyIcon(analytics.averageAccuracy)}
              <Typography variant="h4" color={getAccuracyColor(analytics?.averageAccuracy || 0)}>
                {formatPercentage(analytics?.averageAccuracy || 0)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Estimation accuracy
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <SpeedIcon color="primary" />
              <Typography variant="h6">Team Velocity</Typography>
            </Box>
            <Typography variant="h4" color="primary">
              {velocity?.currentVelocity?.toFixed(1) || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Story points per sprint
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <PersonIcon color="primary" />
              <Typography variant="h6">Best Estimator</Typography>
            </Box>
            <Typography variant="h6" color="primary">
              {insights?.mostAccurateEstimator?.userName || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {insights?.mostAccurateEstimator?.averageAccuracy?.toFixed(1) || 0}% accuracy
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderAccuracyTrends = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Accuracy Trends
        </Typography>
        {analytics?.accuracyTrend?.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell>Average Accuracy</TableCell>
                  <TableCell>Story Count</TableCell>
                  <TableCell>Trend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.accuracyTrend.map((trend: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{trend.period}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getAccuracyIcon(trend.averageAccuracy)}
                        <Typography color={getAccuracyColor(trend.averageAccuracy)}>
                          {formatPercentage(trend.averageAccuracy)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{trend.storyCount}</TableCell>
                    <TableCell>
                      <LinearProgress
                        variant="determinate"
                        value={Math.abs(trend.averageAccuracy)}
                        color={getAccuracyColor(trend.averageAccuracy) as any}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No trend data available
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderTeamBreakdown = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Accuracy by Team
        </Typography>
        {analytics?.accuracyByTeam?.length > 0 ? (
          <Grid container spacing={2}>
            {analytics.accuracyByTeam.map((team: any, index: number) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {team.teamName}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {getAccuracyIcon(team.averageAccuracy)}
                      <Typography variant="h6" color={getAccuracyColor(team.averageAccuracy)}>
                        {formatPercentage(team.averageAccuracy)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {team.storyCount} stories
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No team data available
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderVelocityAnalysis = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Team Velocity Analysis
        </Typography>
        {velocity ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Current Velocity
              </Typography>
              <Typography variant="h4" color="primary">
                {velocity.currentVelocity?.toFixed(1)} points/sprint
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Based on last 2 months
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Confidence Interval (95%)
              </Typography>
              <Typography variant="h6" color="primary">
                {velocity.confidenceInterval?.lower?.toFixed(1)} - {velocity.confidenceInterval?.upper?.toFixed(1)} points
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Range for future sprints
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Historical Velocity
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell>Velocity</TableCell>
                      <TableCell>Story Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {velocity.historicalVelocity?.map((v: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{v.period}</TableCell>
                        <TableCell>{v.velocity?.toFixed(1)} points</TableCell>
                        <TableCell>{v.storyCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Select a team to view velocity analysis
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderEstimationInsights = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Most Overestimated Stories
            </Typography>
            {insights?.mostOverestimatedStories?.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Story</TableCell>
                      <TableCell>Est. Effort</TableCell>
                      <TableCell>Actual Effort</TableCell>
                      <TableCell>Accuracy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {insights.mostOverestimatedStories.slice(0, 5).map((story: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {story.storyTitle}
                          </Typography>
                        </TableCell>
                        <TableCell>{story.estimatedEffort} days</TableCell>
                        <TableCell>{story.actualEffort} days</TableCell>
                        <TableCell>
                          <Chip
                            label={`+${formatPercentage(story.accuracyPercentage)}`}
                            color="error"
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No overestimated stories found
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Most Underestimated Stories
            </Typography>
            {insights?.mostUnderestimatedStories?.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Story</TableCell>
                      <TableCell>Est. Effort</TableCell>
                      <TableCell>Actual Effort</TableCell>
                      <TableCell>Accuracy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {insights.mostUnderestimatedStories.slice(0, 5).map((story: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {story.storyTitle}
                          </Typography>
                        </TableCell>
                        <TableCell>{story.estimatedEffort} days</TableCell>
                        <TableCell>{story.actualEffort} days</TableCell>
                        <TableCell>
                          <Chip
                            label={`-${formatPercentage(story.accuracyPercentage)}`}
                            color="warning"
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No underestimated stories found
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Historical Analytics
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Track estimation accuracy, team velocity, and identify improvement opportunities.
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Team</InputLabel>
                <Select
                  value={selectedTeam}
                  label="Team"
                  onChange={(e) => setSelectedTeam(e.target.value)}
                >
                  <MenuItem value="">All Teams</MenuItem>
                  {teams.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Project</InputLabel>
                <Select
                  value={selectedProject}
                  label="Project"
                  onChange={(e) => setSelectedProject(e.target.value)}
                >
                  <MenuItem value="">All Projects</MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={loadAnalytics}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} /> : 'Refresh'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Overview Cards */}
      {analytics && renderAccuracyOverview()}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Accuracy Trends" />
          <Tab label="Team Breakdown" />
          <Tab label="Velocity Analysis" />
          <Tab label="Estimation Insights" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {renderAccuracyTrends()}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {renderTeamBreakdown()}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {renderVelocityAnalysis()}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {renderEstimationInsights()}
      </TabPanel>
    </Container>
  );
};

export default HistoricalAnalytics;
